<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import api from '@/api';
import { SHAREPOINT_ERROR_MESSAGES } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { SharepointSite } from '@/types';

interface Props {
  isDisabled: boolean;
}
defineProps<Props>();

// このコンポーネントはDgrDialogをラップしているだけなので、openの値をこのコンポーネント内で変更しない。
const open = defineModel<boolean>('open', { default: false });
watch(
  () => open.value,
  () => {
    if (!open.value) {
      site.value = { id: null, site_url: '' };
      emit('onClose');
    }
  },
);

const { createSnackbar } = useSnackbar();

const site = ref<SharepointSite>({
  id: null,
  site_url: '',
});
const isSubmitting = ref(false);

const emit = defineEmits<{
  onClose: [void];
  onUpdated: [void];
}>();

const getSharepointSites = async () => {
  const response = await api.getSharepointSites();
  return response;
};

const submit = async () => {
  isSubmitting.value = true;
  try {
    await api.postOneSharepointSite(site.value.site_url);
    getSharepointSite();
    createSnackbar({
      message: 'サイトを更新しました',
      type: 'success',
    });
    emit('onUpdated');
    open.value = false;
  } catch (error) {
    let errorMessage = 'サイトの更新に失敗しました';
    if (isAxiosError(error)) {
      const serverError: string = error.response?.data.error;
      if (serverError in SHAREPOINT_ERROR_MESSAGES) {
        errorMessage = SHAREPOINT_ERROR_MESSAGES[serverError];
      }
    }
    createSnackbar({
      message: errorMessage,
      type: 'error',
    });

    // eslint-disable-next-line no-console
    console.error(error);
  }
  isSubmitting.value = false;
};

const getSharepointSite = async () => {
  const response = await getSharepointSites();
  site.value = {
    id: null,
    site_url: '',
  };
  if (response.length > 0) {
    site.value = response[0];
  }
};

onMounted(async () => {
  getSharepointSite();
});
</script>

<template>
  <DgrDialog v-model:open="open" title="サイトを編集">
    <template #description>
      <div class="modal-body">
        <div class="modal-form">
          <label class="c-title-bold--m" for="site-url-form">サイトURL</label>
          <div>
            <input type="text" v-model="site.site_url" id="site-url-form" />
            <div class="text-warning c-text-regular--s" v-if="isDisabled">
              現在はサイトURLを変更できません
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #submit>
      <LoadableButton
        class="modal-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: isDisabled }"
        :disabled="isDisabled"
        @click="submit"
        :is-loading="isSubmitting"
      >
        保存
      </LoadableButton>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.modal-body {
  width: 568px; // 600 - padding * 2
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  .modal-form {
    padding: 9px 8px 0px 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    .current-client-secret {
      color: $color-gray800;
    }
    input {
      width: 500px;
    }
    .text-warning {
      margin-top: 25px;
      color: $color-orange1000;
    }
  }
}
</style>
